<script>
	import Select, { Option } from "@smui/select";
	import Checkbox from "@smui/checkbox";
	import FormField from "@smui/form-field";
	import Accordion, {
		Panel,
		Header as AccordionHeader,
		Content as AccordionContent,
	} from "@smui-extra/accordion";
	import { flattenOptions, isGroupedOptions } from "../../../utils";

	export let multi;
	export let id;
	export let name;
	export let options;
	export let selected;
	export let modified;
	export let original;
	export let required;
	export let min = undefined;
	export let max = undefined;
	export let accordion = false;
	export let group_order = undefined;

	$: grouped = isGroupedOptions(options);
	$: allValues = flattenOptions(options);
	$: groups = grouped
		? (group_order
			? group_order.map(name => [name, options[name]])
			: Object.entries(options))
		: null;
	$: atMax = max != null && selected.length >= max;
	$: atMin = min != null && selected.length <= min;

	$: modified = multi ? JSON.stringify(selected) !== JSON.stringify(original) : selected !== original;

	function countInGroup(groupValues) {
		return groupValues.filter((v) => selected.includes(v)).length;
	}

	function isDisabled(value) {
		const isChecked = selected.includes(value);
		if (atMax && !isChecked) return true;
		if (atMin && isChecked) return true;
		return false;
	}
</script>

<div class="option" class:multi class:single={!multi}>
	<div class="option-title"><big>{name || id}</big></div>

	{#if multi && (min != null || max != null)}
		<div class="selection-info" class:below-min={min != null && selected.length < min}>
			{selected.length} selected{#if min != null && max != null} (min {min}, max {max}){:else if min != null} (min {min}){:else if max != null} (max {max}){/if}
		</div>
	{/if}

	<div class="option-element">
		{#if multi}
			{#if grouped && accordion}
				<Accordion>
					{#each groups as [groupName, groupValues]}
						{@const groupCount = countInGroup(groupValues)}
						<Panel>
							<AccordionHeader>
								<span>
									{groupName}
									{#if groupCount > 0}
										<span class="group-count">{groupCount}</span>
									{/if}
								</span>
							</AccordionHeader>
							<AccordionContent>
								{#each groupValues as v}
									<FormField>
										<Checkbox bind:group={selected} value={v} disabled={isDisabled(v)} />
										<span slot="label">{v}</span>
									</FormField>
								{/each}
							</AccordionContent>
						</Panel>
					{/each}
				</Accordion>
			{:else if grouped}
				{#each groups as [groupName, groupValues]}
					<div class="group">
						<div class="group-heading">{groupName}</div>
						{#each groupValues as v}
							<FormField>
								<Checkbox bind:group={selected} value={v} disabled={isDisabled(v)} />
								<span slot="label">{v}</span>
							</FormField>
						{/each}
					</div>
				{/each}
			{:else}
				{#each allValues as o}
					<FormField>
						<Checkbox bind:group={selected} value={o} disabled={isDisabled(o)} />
						<span slot="label">{o}</span>
					</FormField>
				{/each}
			{/if}
		{:else}
			<Select bind:value={selected}>
				{#each allValues as o}
					<Option value={o}>{o}</Option>
				{/each}
				{#if !required}
					<Option value={null}>-----</Option>
				{/if}
			</Select>
		{/if}
	</div>
</div>

<style lang="scss">
	.option {
		&.single {
			max-width: 500px;
		}

		&.multi {
			.option-title {
				margin-bottom: 12px;
			}
		}
	}

	.selection-info {
		font-size: 0.85em;
		color: var(--mdc-theme-text-secondary-on-background, rgba(0, 0, 0, 0.6));
		margin-bottom: 8px;

		&.below-min {
			color: var(--mdc-theme-error, #b00020);
		}
	}

	.group {
		margin-bottom: 12px;
	}

	.group-heading {
		font-weight: bold;
		font-size: 0.85em;
		margin: 8px 0 4px;
		padding-bottom: 4px;
		border-bottom: 1px solid var(--mdc-theme-text-hint-on-background, rgba(0, 0, 0, 0.1));
	}

	.group-count {
		font-size: 0.8em;
		background: var(--mdc-theme-primary, #6200ee);
		color: #fff;
		border-radius: 10px;
		padding: 1px 8px;
		margin-left: 8px;
	}

	:global(.option .smui-accordion__panel) {
		margin-bottom: 0;
	}

	:global(.option .smui-accordion__header) {
		min-height: 36px;
	}
</style>
